import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  GitBranch,
  History,
  RotateCcw,
  Save,
} from "lucide-react";
import { api } from "../api";
import type {
  ScriptRevision,
  ScriptVersion,
  ScriptVersionTrack,
  Shot,
} from "../types";

interface ContentVersionEditorProps {
  productId: string;
  mode: "script" | "storyboard";
  script: ScriptVersion;
  track: ScriptVersionTrack;
  onSaved: () => Promise<unknown>;
  onUseForGeneration: () => void;
}

function cloneScript(script: ScriptVersion): ScriptVersion {
  return structuredClone(script);
}

function formatTimestamp(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

export function ContentVersionEditor({
  productId,
  mode,
  script,
  track,
  onSaved,
  onUseForGeneration,
}: ContentVersionEditorProps) {
  const currentRevision = track.revisions.find(
    (revision) => revision.revision_id === track.current_revision_id,
  );
  const [draft, setDraft] = useState(() => cloneScript(script));
  const [selectedRevisionId, setSelectedRevisionId] = useState(
    track.current_revision_id,
  );
  const [changeNote, setChangeNote] = useState("");
  const [restoredFrom, setRestoredFrom] = useState("");
  const [feedback, setFeedback] = useState("");

  const selectedRevision = useMemo(
    () =>
      track.revisions.find(
        (revision) => revision.revision_id === selectedRevisionId,
      ) ?? currentRevision,
    [currentRevision, selectedRevisionId, track.revisions],
  );
  const isViewingHistory =
    selectedRevision?.revision_id !== track.current_revision_id;
  const visibleScript = isViewingHistory
    ? selectedRevision?.snapshot ?? draft
    : draft;
  const isDirty = currentRevision
    ? JSON.stringify(draft) !== JSON.stringify(currentRevision.snapshot)
    : false;

  const saveMutation = useMutation({
    mutationFn: () =>
      api.saveScriptRevision(productId, {
        base_revision_id: track.current_revision_id,
        change_note: changeNote,
        script: draft,
      }),
    onSuccess: async (result) => {
      setFeedback(
        result.created
          ? `已保存为 R${String(result.revision.revision_no).padStart(2, "0")}`
          : "内容没有变化，未创建新版本",
      );
      setChangeNote("");
      setRestoredFrom("");
      await onSaved();
    },
  });

  function patchScript(patch: Partial<ScriptVersion>) {
    setDraft((current) => ({ ...current, ...patch }));
    setFeedback("");
  }

  function patchShot(shotNo: number, patch: Partial<Shot>) {
    setDraft((current) => ({
      ...current,
      storyboard: current.storyboard.map((shot) =>
        shot.shot_no === shotNo ? { ...shot, ...patch } : shot,
      ),
    }));
    setFeedback("");
  }

  function restoreRevision(revision: ScriptRevision) {
    setDraft(cloneScript(revision.snapshot));
    setSelectedRevisionId(track.current_revision_id);
    setRestoredFrom(revision.revision_id);
    setChangeNote(`基于 ${revision.revision_id} 恢复并继续编辑`);
    setFeedback("");
  }

  return (
    <section className="content-version-workbench">
      <div className="content-editor-column">
        <header className="content-editor-toolbar">
          <div>
            <span className="document-label">
              {mode === "script" ? "SCRIPT EDITOR" : "STORYBOARD EDITOR"}
            </span>
            <strong>
              {isViewingHistory
                ? `查看历史版本 ${selectedRevision?.revision_id}`
                : `编辑当前版本 ${track.current_revision_id}`}
            </strong>
          </div>
          <div className="editor-state-tags">
            {restoredFrom && <span>已载入 {restoredFrom}</span>}
            <span className={isDirty ? "is-dirty" : ""}>
              {isDirty ? "有未保存修改" : "已同步"}
            </span>
          </div>
        </header>

        {isViewingHistory && selectedRevision ? (
          <div className="historical-version-notice">
            <History size={17} aria-hidden="true" />
            <div>
              <strong>这是只读历史快照</strong>
              <span>历史不会被覆盖；可载入为新草稿，保存后形成新的修订版。</span>
            </div>
            <button
              type="button"
              onClick={() => restoreRevision(selectedRevision)}
            >
              <RotateCcw size={14} aria-hidden="true" />
              基于此版新建
            </button>
          </div>
        ) : null}

        {mode === "script" ? (
          <div className="script-editor-form">
            <EditorField label="视频标题">
              <input
                value={visibleScript.title}
                disabled={isViewingHistory}
                onChange={(event) => patchScript({ title: event.target.value })}
              />
            </EditorField>
            <EditorField label="3 秒钩子">
              <textarea
                rows={2}
                value={visibleScript.hook_3s}
                disabled={isViewingHistory}
                onChange={(event) =>
                  patchScript({ hook_3s: event.target.value })
                }
              />
            </EditorField>
            <div className="editor-field-grid is-compact">
              <EditorField label="钩子类型"><input value={visibleScript.hook_type} disabled={isViewingHistory} onChange={(event) => patchScript({ hook_type: event.target.value })} /></EditorField>
              <EditorField label="CTA"><input value={visibleScript.cta} disabled={isViewingHistory} onChange={(event) => patchScript({ cta: event.target.value })} /></EditorField>
            </div>
            <EditorField label="完整口播稿">
              <textarea
                rows={7}
                value={visibleScript.voiceover}
                disabled={isViewingHistory}
                onChange={(event) =>
                  patchScript({ voiceover: event.target.value })
                }
              />
            </EditorField>
            <EditorField label="字幕文案（每行一条）">
              <textarea
                rows={7}
                value={visibleScript.subtitles.join("\n")}
                disabled={isViewingHistory}
                onChange={(event) =>
                  patchScript({
                    subtitles: event.target.value
                      .split("\n")
                      .map((item) => item.trim())
                      .filter(Boolean),
                  })
                }
              />
            </EditorField>
            <EditorField label="视频生成 Prompt">
              <textarea
                rows={6}
                value={visibleScript.video_prompt}
                disabled={isViewingHistory}
                onChange={(event) =>
                  patchScript({ video_prompt: event.target.value })
                }
              />
            </EditorField>
            <div className="editor-field-grid">
              <EditorField label="数字人口播稿">
                <textarea
                  rows={5}
                  value={visibleScript.avatar_script}
                  disabled={isViewingHistory}
                  onChange={(event) =>
                    patchScript({ avatar_script: event.target.value })
                  }
                />
              </EditorField>
              <EditorField label="配音与节奏建议">
                <textarea
                  rows={5}
                  value={visibleScript.voice_advice}
                  disabled={isViewingHistory}
                  onChange={(event) =>
                    patchScript({ voice_advice: event.target.value })
                  }
                />
              </EditorField>
            </div>
          </div>
        ) : (
          <div className="storyboard-editor-list">
            {visibleScript.storyboard.map((shot) => (
              <article className="storyboard-editor-card" key={shot.shot_no}>
                <div className="storyboard-card-index">
                  <span>SHOT</span>
                  <strong>{String(shot.shot_no).padStart(2, "0")}</strong>
                  <small>
                    镜头 R
                    {selectedRevision?.shot_revision_map[
                      String(shot.shot_no)
                    ] ?? 1}
                  </small>
                </div>
                <div className="storyboard-card-fields">
                  <div className="editor-field-grid is-compact">
                    <EditorField label="时间段">
                      <input
                        value={shot.time_range}
                        disabled={isViewingHistory}
                        onChange={(event) =>
                          patchShot(shot.shot_no, {
                            time_range: event.target.value,
                          })
                        }
                      />
                    </EditorField>
                    <EditorField label="运镜">
                      <input
                        value={shot.camera}
                        disabled={isViewingHistory}
                        onChange={(event) =>
                          patchShot(shot.shot_no, {
                            camera: event.target.value,
                          })
                        }
                      />
                    </EditorField>
                  </div>
                  <EditorField label="画面描述">
                    <textarea
                      rows={3}
                      value={shot.visual}
                      disabled={isViewingHistory}
                      onChange={(event) =>
                        patchShot(shot.shot_no, {
                          visual: event.target.value,
                        })
                      }
                    />
                  </EditorField>
                  <EditorField label="镜头口播">
                    <textarea
                      rows={2}
                      value={shot.voiceover}
                      disabled={isViewingHistory}
                      onChange={(event) =>
                        patchShot(shot.shot_no, {
                          voiceover: event.target.value,
                        })
                      }
                    />
                  </EditorField>
                  <div className="editor-field-grid is-compact">
                    <EditorField label="首帧设计"><input value={shot.first_frame} disabled={isViewingHistory} onChange={(event) => patchShot(shot.shot_no, { first_frame: event.target.value })} /></EditorField>
                    <EditorField label="对应卖点"><input value={shot.selling_point_id} disabled={isViewingHistory} onChange={(event) => patchShot(shot.shot_no, { selling_point_id: event.target.value })} /></EditorField>
                  </div>
                  <EditorField label="画面如何证明卖点"><textarea rows={2} value={shot.visual_proof} disabled={isViewingHistory} onChange={(event) => patchShot(shot.shot_no, { visual_proof: event.target.value })} /></EditorField>
                  <div className="editor-field-grid is-compact">
                    <EditorField label="画面字幕">
                      <input
                        value={shot.subtitle}
                        disabled={isViewingHistory}
                        onChange={(event) =>
                          patchShot(shot.shot_no, {
                            subtitle: event.target.value,
                          })
                        }
                      />
                    </EditorField>
                    <EditorField label="素材来源">
                      <input
                        value={shot.asset_source}
                        disabled={isViewingHistory}
                        onChange={(event) =>
                          patchShot(shot.shot_no, {
                            asset_source: event.target.value,
                          })
                        }
                      />
                    </EditorField>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {!isViewingHistory && (
          <footer className="content-save-bar">
            <label>
              <span>本次修改说明</span>
              <input
                value={changeNote}
                placeholder="例如：调整前 3 秒钩子与镜头 2 的产品展示"
                onChange={(event) => setChangeNote(event.target.value)}
              />
            </label>
            <button
              type="button"
              disabled={!isDirty || !changeNote.trim() || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              <Save size={15} aria-hidden="true" />
              {saveMutation.isPending ? "正在保存" : "保存为新版本"}
            </button>
            <button
              type="button"
              className="secondary-button"
              disabled={isDirty || isViewingHistory}
              onClick={onUseForGeneration}
            >
              <GitBranch size={15} aria-hidden="true" />
              使用此版本生成
            </button>
          </footer>
        )}

        {saveMutation.isError && (
          <p className="version-feedback is-error">
            <AlertTriangle size={15} aria-hidden="true" />
            {saveMutation.error.message}
          </p>
        )}
        {feedback && (
          <p className="version-feedback is-success">
            <CheckCircle2 size={15} aria-hidden="true" />
            {feedback}
          </p>
        )}
      </div>

      <aside className="revision-rail">
        <div className="revision-rail-heading">
          <History size={17} aria-hidden="true" />
          <div>
            <strong>脚本修订记录</strong>
            <span>{track.revisions.length} 个不可变版本</span>
          </div>
        </div>
        <div className="revision-list">
          {track.revisions.map((revision) => {
            const isCurrent =
              revision.revision_id === track.current_revision_id;
            const isSelected =
              revision.revision_id === selectedRevision?.revision_id;
            return (
              <button
                type="button"
                key={revision.revision_id}
                className={isSelected ? "is-active" : ""}
                onClick={() => setSelectedRevisionId(revision.revision_id)}
              >
                <div>
                  <strong>
                    R{String(revision.revision_no).padStart(2, "0")}
                  </strong>
                  {isCurrent && <span>当前</span>}
                </div>
                <p>{revision.change_note}</p>
                <small>
                  {formatTimestamp(revision.created_at)} ·{" "}
                  {revision.changed_fields.join(" / ")}
                </small>
              </button>
            );
          })}
        </div>
        <p className="revision-policy-note">
          保存只创建新修订，不会发起任何视频、数字人或配音付费调用。
        </p>
      </aside>
    </section>
  );
}

function EditorField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="version-editor-field">
      <span>{label}</span>
      {children}
    </label>
  );
}
